import { useState } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Card, CardContent, CardTitle } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Chip } from '@/components/ui/Chip'
import { Crown, Trophy, Handshake } from 'lucide-react'
import { projects, projectCategories } from '@/data/projects'

export function Projects() {
  const [selectedFilter, setSelectedFilter] = useState('all')
  const [showAll, setShowAll] = useState(false)
  const [expandedSkills, setExpandedSkills] = useState<Set<string>>(new Set())

  const filteredProjects = projects.filter(project => 
    selectedFilter === 'all' || project.category === selectedFilter
  )

  // Show only 6 projects initially, or all if showAll is true
  const displayedProjects = showAll ? filteredProjects : filteredProjects.slice(0, 6)

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'completed':
        return 'default'
      case 'in-progress':
        return 'secondary'
      case 'prototype':
        return 'outline'
      case 'archived':
        return 'destructive'
      default:
        return 'default'
    }
  }

  const toggleSkillsExpansion = (projectId: string) => {
    setExpandedSkills(prev => {
      const newSet = new Set(prev)
      if (newSet.has(projectId)) {
        newSet.delete(projectId)
      } else {
        newSet.add(projectId)
      }
      return newSet
    })
  }

  const renderProjectImage = (project: any) => {
    if (project.image === 'chess-piece') {
      return (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900">
          <Crown className="h-24 w-24 text-slate-600 dark:text-slate-400" />
        </div>
      )
    }
    return (
      <img
        src={project.image}
        alt={project.title}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
      />
    )
  }

  const renderTechnologies = (project: any) => {
    const isExpanded = expandedSkills.has(project.id)
    const technologies = project.technologies
    const visibleTech = isExpanded ? technologies : technologies.slice(0, 6)
    const hasMore = technologies.length > 6

    return (
      <div className="flex flex-wrap gap-2 mb-4">
        {visibleTech.map((tech: string, index: number) => (
          <Chip key={index} variant="outline" size="sm">
            {tech}
          </Chip>
        ))}
        {hasMore && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
            onClick={(e) => {
              e.stopPropagation()
              toggleSkillsExpansion(project.id)
            }}
          >
            {isExpanded ? 'Show Less' : `+${technologies.length - 6} More`}
          </Button>
        )}
      </div>
    )
  }

  return (
    <section id="projects" className="py-20">
      <div className="container mx-auto px-4">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold mb-4">Projects</h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            A collection of my work across different domains and technologies
          </p>
        </motion.div>

        {/* Filter chips */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          className="flex flex-wrap justify-center gap-3 mb-12"
        >
          {projectCategories.map((category) => (
            <Chip
              key={category.id}
              variant={selectedFilter === category.id ? 'default' : 'outline'}
              className="cursor-pointer hover:bg-accent transition-colors"
              onClick={() => setSelectedFilter(category.id)}
            >
              {category.label} ({category.count})
            </Chip>
          ))}
        </motion.div>

        {/* Projects grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {displayedProjects.map((project, index) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
              viewport={{ once: true }}
              className="h-full"
            >
              {project.id === 'airbnb-project' ? (
                <Card className="h-full group overflow-hidden hover:card-hover transition-all duration-300 flex flex-col">
                  {/* Project image */}
                  <div className="relative h-48 overflow-hidden">
                    {renderProjectImage(project)}
                    {project.featured && (
                      <div className="absolute top-4 left-4">
                        <Badge variant="secondary">Featured</Badge>
                      </div>
                    )}
                    <div className="absolute top-4 right-4">
                      <Badge variant={getStatusVariant(project.status)}>
                        {project.status === 'completed' ? 'Live' : 
                         project.status === 'in-progress' ? 'In Progress' : 
                         project.status === 'prototype' ? 'Prototype' :
                         'Archived'}
                      </Badge>
                    </div>
                    {project.id === 'airbnb-project' && (
                      <div className="absolute bottom-3 right-3 flex items-center space-x-2">
                        <img src="/logos/nyu-logo.png" alt="NYU" className="h-12 w-auto" />
                      </div>
                    )}
                  </div>

                  <CardContent className="p-6 flex flex-col flex-grow">
                    <CardTitle className="text-xl mb-2">{project.title}</CardTitle>
                    <p className="text-muted-foreground mb-4 flex-grow">{project.description}</p>
                    
                    {/* Technologies */}
                    {renderTechnologies(project)}

                    {/* Action buttons - consistent placement at bottom */}
                    <div className="mt-auto space-y-2">
                      <div className="flex gap-2">
                        {project.links.github && (
                          <Button
                            variant="secondary"
                            size="sm"
                            className="w-full group/btn hover:scale-105 hover:shadow-lg transition-all duration-200 ease-out hover:bg-accent hover:text-accent-contrast"
                            onClick={() => window.open(project.links.github, '_blank')}
                          >
                            <svg className="h-4 w-4 mr-2 group-hover/btn:rotate-12 transition-transform duration-200" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.30.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.931-5.467 5.931-.55.305-1.163.367-1.783.345.418.367.813.824 1.131 1.362.367.55.55 1.131.55 1.783z"/>
                            </svg>
                            View Analysis
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ) : project.id === 'fin4903' ? (
                <Link to={`/projects/${project.id}`} className="block h-full">
                  <Card className="h-full group overflow-hidden hover:card-hover transition-all duration-300 cursor-pointer flex flex-col">
                    {/* Project image */}
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={project.image}
                        alt={project.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      {project.featured && (
                        <div className="absolute top-4 left-4">
                          <Badge variant="secondary">Featured</Badge>
                        </div>
                      )}
                      <div className="absolute top-4 right-4">
                        <Badge variant={getStatusVariant(project.status)}>
                          {project.status === 'completed' ? 'Live' : 
                           project.status === 'in-progress' ? 'In Progress' : 
                           project.status === 'prototype' ? 'Prototype' :
                           'Archived'}
                        </Badge>
                      </div>
                      
                      {/* Project-specific logos */}
                      {project.id === 'fin4903' && (
                        <div className="absolute bottom-3 right-3 flex items-center space-x-2">
                          <img src="/logos/nyu-logo.png" alt="NYU" className="h-12 w-auto" />
                        </div>
                      )}
                    </div>

                    <CardContent className="p-6 flex flex-col flex-grow">
                      <CardTitle className="text-xl mb-2">{project.title}</CardTitle>
                      <p className="text-muted-foreground mb-4 flex-grow">{project.description}</p>
                      
                      {/* Technologies */}
                      {renderTechnologies(project)}

                      {/* Action buttons - consistent placement at bottom */}
                      <div className="mt-auto space-y-2">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="flex-1 group/btn hover:scale-105 hover:shadow-lg transition-all duration-200 ease-out hover:bg-accent hover:text-accent-contrast"
                          >
                            View Details
                            <svg className="h-4 w-4 ml-2 group-hover/btn:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                          </Button>

                          {project.links.github && (
                            <Button
                              variant="secondary"
                              size="sm"
                              className="group/btn hover:scale-105 hover:shadow-lg transition-all duration-200 ease-out hover:bg-accent hover:text-accent-contrast"
                              onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                window.open(project.links.github, '_blank')
                              }}
                            >
                              <svg className="h-4 w-4 group-hover/btn:rotate-12 transition-transform duration-200" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.30.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.931-5.467 5.931-.55.305-1.163.367-1.783.345.418.367.813.824 1.131 1.362.367.55.55 1.131.55 1.783z"/>
                              </svg>
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ) : project.id === 'book-blockchain' ? (
                <Card className="h-full group overflow-hidden hover:card-hover transition-all duration-300 flex flex-col">
                  {/* Project image */}
                  <div className="relative h-48 overflow-hidden bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 p-4">
                    {/* Book cover container with professional styling */}
                    <div className="relative w-full h-full flex items-center justify-center">
                      <div className="relative w-32 h-42 shadow-2xl group-hover:shadow-3xl transition-all duration-300 group-hover:scale-105">
                        {/* Book cover with 3D effect */}
                        {project.image === 'chess-piece' ? (
                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 rounded-sm">
                            <Crown className="h-16 w-16 text-slate-600 dark:text-slate-400" />
                          </div>
                        ) : (
                          <img
                            src={project.image}
                            alt={project.title}
                            className="w-full h-full object-cover rounded-sm shadow-lg"
                          />
                        )}
                        {/* Book spine shadow */}
                        <div className="absolute -left-1 top-0 w-1 h-full bg-gradient-to-b from-slate-600 to-slate-800 rounded-l-sm"></div>
                        {/* Book pages effect */}
                        <div className="absolute -right-1 top-0 w-1 h-full bg-gradient-to-b from-slate-200 to-slate-400 rounded-r-sm"></div>
                        {/* Subtle highlight */}
                        <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent rounded-sm"></div>
                      </div>
                    </div>
                    
                    {/* Badges positioned outside the book */}
                    {project.featured && (
                      <div className="absolute top-2 left-2">
                        <Badge variant="secondary" className="shadow-lg">Featured</Badge>
                      </div>
                    )}
                    <div className="absolute top-2 right-2">
                      <Badge variant={getStatusVariant(project.status)} className="shadow-lg">
                        {project.status === 'completed' ? 'Completed' : 
                         project.status === 'in-progress' ? 'In Progress' : 
                         project.status === 'prototype' ? 'Prototype' :
                         project.status === 'published' ? 'Published' :
                         'Archived'}
                      </Badge>
                    </div>
                  </div>

                  <CardContent className="p-6 flex flex-col flex-grow">
                    <CardTitle className="text-xl mb-2">{project.title}</CardTitle>
                    <p className="text-muted-foreground mb-4 flex-grow">{project.description}</p>
                    
                    {/* Technologies */}
                    {renderTechnologies(project)}

                    {/* Action buttons - consistent placement at bottom */}
                    <div className="mt-auto space-y-2">
                      <div className="flex gap-2">
                        {project.links.amazon && (
                          <Button
                            size="sm"
                            className="flex-1 group/btn hover:scale-105 hover:shadow-lg transition-all duration-200 ease-out hover:bg-accent hover:text-accent-contrast"
                            onClick={() => window.open(project.links.amazon, '_blank')}
                          >
                            View on Amazon
                            <svg className="h-4 w-4 ml-2 group-hover/btn:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ) : project.id === 'us-stocks-ta' ? (
                <Card className="h-full group overflow-hidden hover:card-hover transition-all duration-300 flex flex-col">
                  {/* Project image */}
                  <div className="relative h-48 overflow-hidden">
                    {renderProjectImage(project)}
                    {project.featured && (
                      <div className="absolute top-4 left-4">
                        <Badge variant="secondary">Featured</Badge>
                      </div>
                    )}
                    <div className="absolute top-4 right-4">
                      <Badge variant={getStatusVariant(project.status)}>
                        {project.status === 'completed' ? 'Live' : 
                         project.status === 'in-progress' ? 'In Progress' : 
                         project.status === 'prototype' ? 'Prototype' :
                         'Archived'}
                      </Badge>
                    </div>
                    
                    {/* Project-specific logos */}
                    <div className="absolute bottom-3 right-3 flex items-center space-x-2">
                      <img src="/logos/nyu-logo.png" alt="NYU" className="h-12 w-auto" />
                    </div>
                  </div>

                  <CardContent className="p-6 flex flex-col flex-grow">
                    <CardTitle className="text-xl mb-2">{project.title}</CardTitle>
                    <p className="text-muted-foreground mb-4 flex-grow">{project.description}</p>
                    
                    {/* Technologies */}
                    {renderTechnologies(project)}

                    {/* Action buttons - consistent placement at bottom */}
                    <div className="mt-auto space-y-2">
                      <div className="flex gap-2">
                        {project.links.github && (
                          <Button
                            variant="secondary"
                            size="sm"
                            className="w-full group/btn hover:scale-105 hover:shadow-lg transition-all duration-200 ease-out hover:bg-accent hover:text-accent-contrast"
                            onClick={() => window.open(project.links.github, '_blank')}
                          >
                            <svg className="h-4 w-4 mr-2 group-hover/btn:rotate-12 transition-transform duration-200" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.30.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.931-5.467 5.931-.55.305-1.163.367-1.783.345.418.367.813.824 1.131 1.362.367.55.55 1.131.55 1.783z"/>
                            </svg>
                            View Analysis
                          </Button>
                        )}
                      </div>

                      {project.links.caseStudy && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full group/btn hover:scale-105 hover:shadow-lg transition-all duration-200 ease-out hover:bg-accent hover:text-accent-contrast"
                          onClick={() => window.open(project.links.caseStudy, '_blank')}
                        >
                          View Case Study
                          <svg className="h-4 w-4 ml-2 group-hover/btn:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                          </svg>
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className="h-full group overflow-hidden hover:card-hover transition-all duration-300 flex flex-col">
                  {/* Project image */}
                  <div className="relative h-48 overflow-hidden">
                    {renderProjectImage(project)}
                    {project.featured && (
                      <div className="absolute top-4 left-4">
                        <Badge variant="secondary">Featured</Badge>
                      </div>
                    )}
                    <div className="absolute top-4 right-4">
                      <Badge variant={getStatusVariant(project.status)}>
                        {project.status === 'completed' ? 'Live' : 
                         project.status === 'in-progress' ? 'In Progress' : 
                         project.status === 'prototype' ? 'Prototype' :
                         'Archived'}
                      </Badge>
                    </div>
                    
                    {/* Project-specific logos */}
                    {project.id === 'labscoutr' && (
                      <div className="absolute bottom-3 right-3 flex items-center space-x-2">
                        <img src="/logos/nyu-logo.png" alt="NYU" className="h-12 w-auto" />
                        <img src="/logos/cwru.svg" alt="CWRU" className="h-8 w-auto" />
                      </div>
                    )}
                    {project.id === 'certify' && (
                      <div className="absolute bottom-3 right-3 flex items-center space-x-2">
                        <img src="/logos/nyu-logo.png" alt="NYU" className="h-12 w-auto" />
                      </div>
                    )}
                    {project.id === 'zktls-demo' && (
                      <div className="absolute bottom-3 right-3 flex items-center space-x-2">
                        <img src="/logos/zksync.png" alt="ZKsync" className="h-8 w-auto" />
                        <img src="/logos/NYU.B&F.webp" alt="B&F" className="h-8 w-auto" />
                        <img src="/logos/nyu-logo.png" alt="NYU" className="h-12 w-auto" />
                      </div>
                    )}
                  </div>

                  <CardContent className="p-6 flex flex-col flex-grow">
                    <div className="flex items-center justify-between mb-2">
                      <CardTitle className="text-xl">{project.title}</CardTitle>
                      {/* Award chip for LabScoutr - positioned on the right */}
                      {project.id === 'labscoutr' && project.award && (
                        <div className="relative group">
                          <Chip variant="award" size="sm" className="font-semibold cursor-help">
                            <Trophy className="w-4 h-4 mr-1" />
                            {project.award.amount} Award
                          </Chip>
                          {/* Tooltip */}
                          <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                            {project.award.organization}
                            <br />
                            {project.award.program}
                            {/* Tooltip arrow */}
                            <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-100"></div>
                          </div>
                        </div>
                      )}
                    </div>
                    <p className="text-muted-foreground mb-4 flex-grow">{project.description}</p>
                    
                    {/* Certification for LabScoutr and RevSplit Finance */}
                    {project.certification && (project.id === 'labscoutr' || project.id === 'revsplit-finance') && (
                      <div className="mb-4">
                        <Button 
                          variant="certification" 
                          size="sm" 
                          className="text-xs font-medium"
                          disabled
                        >
                          ✓ {project.certification.type}
                        </Button>
                      </div>
                    )}

                    {/* Partnership for EigenCloud Development */}
                    {project.partnership && project.id === 'eigencloud-development' && (
                      <div className="mb-4">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="text-xs font-medium"
                          onClick={() => window.open(project.partnership?.url, '_blank')}
                        >
                          <Handshake className="w-3 h-3 mr-1" />
                          Partnership with {project.partnership.organization}
                        </Button>
                      </div>
                    )}
                    
                    {/* Technologies */}
                    {renderTechnologies(project)}

                    {/* Action buttons - consistent placement at bottom */}
                    <div className="mt-auto space-y-2">
                      <div className="flex gap-2">
                        {project.status === 'prototype' ? (
                          <Button
                            size="sm"
                            className="flex-1"
                            disabled
                            variant="secondary"
                          >
                            Prototype
                          </Button>
                        ) : project.links.live ? (
                          <Button
                            size="sm"
                            className="flex-1 group/btn hover:scale-105 hover:shadow-lg transition-all duration-200 ease-out hover:bg-accent hover:text-accent-contrast"
                            onClick={() => window.open(project.links.live, '_blank')}
                          >
                            Live Demo
                            <svg className="h-4 w-4 ml-2 group-hover/btn:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            className="flex-1 group/btn hover:scale-105 hover:shadow-lg transition-all duration-200 ease-out hover:bg-accent hover:text-accent-contrast"
                            disabled
                          >
                            View Details
                            <svg className="h-4 w-4 ml-2 group-hover/btn:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                          </Button>
                        )}

                        {project.links.github && (
                          <Button
                            variant="secondary"
                            size="sm"
                            className="group/btn hover:scale-105 hover:shadow-lg transition-all duration-200 ease-out hover:bg-accent hover:text-accent-contrast"
                            onClick={() => window.open(project.links.github, '_blank')}
                          >
                            <svg className="h-4 w-4 group-hover/btn:rotate-12 transition-transform duration-200" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.30.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.931-5.467 5.931-.55.305-1.163.367-1.783.345.418.367.813.824 1.131 1.362.367.55.55 1.131.55 1.783z"/>
                            </svg>
                          </Button>
                        )}
                      </div>

                      {project.links.caseStudy && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full group/btn hover:scale-105 hover:shadow-lg transition-all duration-200 ease-out hover:bg-accent hover:text-accent-contrast"
                          onClick={() => window.open(project.links.caseStudy, '_blank')}
                        >
                          View Case Study
                          <svg className="h-4 w-4 ml-2 group-hover/btn:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                          </svg>
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </motion.div>
          ))}
        </div>

        {/* Show all projects button */}
        {!showAll && filteredProjects.length > 6 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Button
              onClick={() => setShowAll(true)}
              className="group/btn hover:scale-105 hover:shadow-lg transition-all duration-200 ease-out"
            >
              View All Projects
              <svg className="h-4 w-4 ml-2 group-hover/btn:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Button>
          </motion.div>
        )}
      </div>
    </section>
  )
}